import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Cloud, Heart, Smile } from "lucide-react";
import { api } from "../lib/api";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import YarnScene from "../components/YarnScene";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Heart as HeartIcon, Scallop, Sparkle } from "../components/Decor";
import Seo from "../components/Seo";

function HeroLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { settings } = useStore();
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -30]);

  useEffect(() => {
    api("/products").then((all) => {
      const featured = all.filter((p) => p.featured);
      setProducts((featured.length >= 3 ? featured : all).slice(0, 3));
    }).catch(() => {});
  }, []);

  const headline = (settings.hero_headline || "Little stitches. Big happy feelings.").split(". ").filter(Boolean);

  return (
    <div>
      <Seo />
      {/* HERO */}
      <section ref={heroRef} className="hero-sky relative overflow-hidden grain" data-testid="hero-section">
        <div className="cloud-blob w-72 h-24 -left-10 top-16" />
        <div className="cloud-blob w-96 h-28 right-1/4 top-6" />
        <div className="cloud-blob w-80 h-24 left-1/3 bottom-10" />
        <HeartIcon className="absolute left-[6%] top-[22%] w-6 h-6 text-lilac-accent/60 yr-anim" style={{ animation: "yr-heartbeat 4s ease-in-out infinite" }} />
        <Sparkle className="absolute left-[42%] top-[12%] w-5 h-5 text-butter-accent yr-anim" style={{ animation: "yr-bob 6s ease-in-out infinite" }} />
        <HeartIcon className="absolute right-[4%] top-[40%] w-8 h-8 text-candy/50 yr-anim" style={{ animation: "yr-heartbeat 5s ease-in-out infinite" }} />
        <Sparkle className="absolute right-[30%] bottom-[18%] w-6 h-6 text-mint-accent/70 yr-anim" style={{ animation: "yr-bob 7s ease-in-out infinite" }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-24 grid md:grid-cols-2 gap-10 items-center relative">
          <motion.div style={{ y: copyY }} className="text-center md:text-left">
            <h1 data-testid="hero-headline" className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-ink -rotate-2">
              {headline.map((line, i) => (
                <HeroLine key={i} delay={0.15 + i * 0.18}>
                  {line}{i < headline.length - 1 ? "." : ""}
                </HeroLine>
              ))}
              <HeroLine delay={0.15 + headline.length * 0.18}>
                <HeartIcon className="inline w-9 h-9 lg:w-11 lg:h-11 text-candy ml-2" />
              </HeroLine>
            </h1>
            <motion.p data-testid="hero-subtitle" className="mt-5 text-lg text-ink/70 font-semibold" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}>
              {settings.hero_subtitle || "Colorful crochet creations, handmade by Rachel."}
            </motion.p>
            <motion.div
              className="mt-8 flex flex-col sm:flex-row items-center gap-4 md:justify-start justify-center"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            >
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-candy hover:bg-candy-deep text-white font-display font-semibold text-lg shadow-pinkGlow transition-colors duration-200"
              >
                {settings.hero_cta || "Shop the cozy collection"}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link to="/meet-rachel" data-testid="hero-meet-maker-link" className="font-bold text-ink underline decoration-candy decoration-2 underline-offset-4 hover:text-candy-deep transition-colors duration-200">
                Meet the maker →
              </Link>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: artY }} className="relative">
            <motion.img
              src="/assets/hero-art.jpg"
              alt="The Pastel Cloud Bag and Cuddle Teddy sitting on fluffy clouds with pastel yarn balls"
              data-testid="hero-art-image"
              className="w-full max-w-xl mx-auto rounded-cloud shadow-card"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <Scallop fill="#fce7f3" dir="up" />
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="bg-candy-soft" data-testid="values-strip">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-0">
          {[
            { icon: Heart, label: "Handmade with love", iconCls: "text-candy" },
            { icon: Cloud, label: "Soft & snuggly", iconCls: "text-sky-400" },
            { icon: Smile, label: "Made to make you smile", iconCls: "text-lilac-accent" },
          ].map((v, i) => (
            <Reveal key={v.label} delay={i * 0.12} className={`flex items-center gap-3 sm:px-10 ${i > 0 ? "sm:border-l-2 sm:border-candy-soft/100 border-white" : ""}`}>
              <v.icon className={`w-7 h-7 ${v.iconCls}`} />
              <span className="font-display font-semibold text-lg text-ink">{v.label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-candy-mist relative" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20">
          <Reveal className="text-center">
            <h2 data-testid="featured-heading" className="font-display font-semibold text-3xl sm:text-4xl text-ink inline-flex items-center gap-3">
              Pick your next cozy favorite <HeartIcon className="w-7 h-7 text-candy" />
            </h2>
            <div className="mt-6 flex flex-wrap justify-center gap-2" data-testid="featured-tabs">
              {[
                { label: "All creations", to: "/shop" },
                { label: "Bags", to: "/shop/bags" },
                { label: "Plushies", to: "/shop/plushies" },
                { label: "Wearables", to: "/shop/wearables" },
              ].map((t, i) => (
                <Link
                  key={t.label}
                  to={t.to}
                  data-testid={`featured-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-5 py-2 rounded-full text-sm font-bold border transition-colors duration-200 ${i === 0 ? "bg-candy-glow border-candy text-candy-deep" : "bg-white border-candy-soft text-ink/70 hover:bg-candy-soft"}`}
                >
                  {t.label}
                </Link>
              ))}
            </div>
          </Reveal>
          <Stagger className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
            {products.map((p, i) => (
              <StaggerItem key={p.slug}><ProductCard product={p} index={i} /></StaggerItem>
            ))}
          </Stagger>
          {products.length === 0 && (
            <p className="text-center text-muted mt-10">Fresh creations are being stitched right now — check back soon!</p>
          )}
          <Reveal className="text-center mt-10">
            <Link to="/shop" data-testid="explore-all-creations" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-candy hover:bg-candy-deep text-white font-display font-semibold text-lg shadow-pink transition-colors duration-200">
              Explore all creations <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* YARN ANIMATION (M3) */}
      <section className="bg-mint-soft relative" data-testid="yarn-scene-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20">
          <Reveal className="text-center mb-8">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl text-ink">Watch a little yarn become something magical</h2>
            <p className="text-xs font-bold text-ink/40 mt-2 tracking-wide">✎ Animation concept</p>
          </Reveal>
          <Reveal delay={0.15}><YarnScene /></Reveal>
        </div>
        <Scallop fill="#d1fae5" dir="down" className="absolute bottom-0 left-0" />
      </section>

      {/* MEET RACHEL */}
      <section className="bg-lilac-soft relative" data-testid="meet-rachel-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 grid md:grid-cols-2 gap-10 items-center">
          <Reveal className="relative flex justify-center">
            <div className="relative">
              <img src="/assets/rachel.jpg" alt="Rachel crocheting" data-testid="rachel-portrait" className="w-72 h-72 sm:w-80 sm:h-80 object-cover rounded-[46%_54%_52%_48%/48%_46%_54%_52%] border-8 border-white shadow-card" />
              <div className="absolute -right-6 top-2 bg-white rounded-2xl px-4 py-2 shadow-card rotate-6 font-hand text-xl font-semibold text-candy-deep leading-tight" data-testid="rachel-badge">
                Big dreams<br />stitched with love
              </div>
              <HeartIcon className="absolute -left-6 bottom-4 w-8 h-8 text-candy/60" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl text-ink">Hi, I'm Rachel! <HeartIcon className="inline w-7 h-7 text-candy" /></h2>
            <p className="mt-4 text-lg text-ink/70 font-semibold" data-testid="rachel-bio">{settings.rachel_bio || "I'm 10, and I love turning colorful yarn into cozy little creations. Every piece is handmade with care and a little bit of joy."}</p>
            <Link to="/meet-rachel" data-testid="more-about-rachel-button" className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-candy hover:bg-candy-deep text-white font-bold shadow-pink transition-colors duration-200">
              More about Rachel <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CUSTOM ORDERS */}
      <section className="bg-butter-soft relative" data-testid="custom-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl text-ink">{settings.custom_title || "Your colors. Your cozy."} <HeartIcon className="inline w-7 h-7 text-candy" /></h2>
            <p className="mt-4 text-lg text-ink/70 font-semibold" data-testid="custom-subtitle">{settings.custom_subtitle || "Have a colorful idea? Let's make something special."}</p>
            <Link to="/custom-orders" data-testid="custom-cta" className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-candy hover:bg-candy-deep text-white font-bold shadow-pink transition-colors duration-200">
              Request a custom creation <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative flex justify-center">
            <img src="/assets/yarn-pile.jpg" alt="A pile of colorful yarn balls" data-testid="custom-yarn-pile" className="rounded-cloud shadow-card w-full max-w-md object-cover" />
            <div className="absolute right-4 -top-5 bg-white rounded-2xl px-4 py-2 shadow-card rotate-3 font-hand text-xl font-semibold text-candy-deep max-w-[240px] text-center" data-testid="custom-note">
              Different colors, endless possibilities!
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
